import { RAMP_SLOPE } from "../data/entities";
import type { Impact, ImpactHorizontal, ImpactVertical, WorldEntity } from "../data/model";
import { motor as M } from "../data/tuning";
import type { Support } from "./Motor";

/** The car's collision box: short along the road (like the original's capsule) so hits happen when they look like hits. */
export interface CarBox {
  x: number;
  /** centre along the road */
  z: number;
  y: number;
  height: number;
  halfWidth: number;
  halfLength: number;
  vy: number;
}

export const CAR_HALF_LENGTH = 0.6;
const LAND_TOLERANCE = 0.6;

export const entityTop = (e: WorldEntity): number => e.yBase + e.height;

export function overlapX(car: CarBox, e: WorldEntity): number {
  return car.halfWidth + e.width / 2 - Math.abs(car.x - e.x);
}

export function overlapZ(car: CarBox, e: WorldEntity): boolean {
  return e.z < car.z + car.halfLength && e.z + e.depth > car.z - car.halfLength;
}

/**
 * The surface under the car: a roof you are on or about to land on, a ramp's slope, a platform — or the road.
 * Roofs count only when the car is at or above them (minus a landing tolerance); below that they are walls.
 */
export function supportUnder(car: CarBox, entities: WorldEntity[]): Support {
  let best: Support = { height: 0, kind: "road" };
  for (const e of entities) {
    if (!e.active) continue;
    if (e.kind !== "truck" && e.kind !== "mover" && e.kind !== "ramp" && e.kind !== "platform" && e.kind !== "deck") continue;     // oncoming trucks and bridge decks too
    // the original's gap colliders bridge the crack between trucks in neighbouring lanes: any overlap carries you
    if (overlapX(car, e) < 0.02 || !overlapZ(car, e)) continue;
    const top = entityTop(e);
    if (e.kind === "ramp") {
      const t = Math.min(1, Math.max(0, (car.z - e.z) / RAMP_SLOPE));
      const h = e.yBase + e.height * t;
      if (car.y >= h - LAND_TOLERANCE - e.height * 0.25 && h > best.height) best = { height: h, kind: "ramp" };
      continue;
    }
    if (car.y >= top - LAND_TOLERANCE && car.vy <= 0.01 && top >= best.height) {
      best = { height: top, kind: e.kind === "truck" ? "roof" : "platform" };
    }
  }
  return best;
}

/**
 * Classifies a touch between the car and an entity the way the original's CharacterCollision does.
 * Vertical, measured on the CAR: the centre of the overlap above 66 % of the car's height = head; else the obstacle's
 * top within 72 % of the car's height above its feet = legs (it can get over the edge: a trip); else centre.
 * Horizontal: the car already beside it (overlapping along the road last frame) = a side contact; during a lane change,
 * the car's centre within 1.13 m of the obstacle's edge = a corner; else frontal.
 * Returns null when the boxes do not touch, or when the car is above (riding) or below (ducked under) the entity.
 */
export function classify(car: CarBox, e: WorldEntity, sideEntry: boolean, changingLane = false): Impact | null {
  if (!overlapZ(car, e)) return null;
  const ox = overlapX(car, e);
  if (ox <= 0) return null;
  const top = entityTop(e);
  const carTop = car.y + car.height;
  if (car.y >= top - 0.05) return null;               // above it
  if (carTop <= e.yBase + 0.02) return null;          // under it
  const centre = (Math.max(car.y, e.yBase) + Math.min(carTop, top)) / 2;
  let vertical: ImpactVertical;
  if ((centre - car.y) / car.height > M.upperImpactMin) vertical = "head";
  else if (top <= car.y + M.lowerImpactRatio * car.height) vertical = "legs";
  else vertical = "center";
  let horizontal: ImpactHorizontal;
  const leftOfIt = car.x < e.x;
  const edge = leftOfIt ? e.x - e.width / 2 : e.x + e.width / 2;
  if (sideEntry) horizontal = leftOfIt ? "left" : "right";
  else if (changingLane && Math.abs(car.x - edge) < M.cornerRegionWidth) horizontal = leftOfIt ? "cornerLeft" : "cornerRight";
  else horizontal = "frontal";
  return { entity: e, horizontal, vertical };
}

/** Height of an entity's walkable top under the car (ramps slope over their first 8.6 m). */
export function surfaceTop(e: WorldEntity, carZ: number): number {
  if (e.kind === "ramp") return e.yBase + e.height * Math.min(1, Math.max(0, (carZ - e.z) / RAMP_SLOPE));
  return e.yBase + e.height;
}
